<template>
    <a-modal
        v-model:open="modalOpen"
        :title="`Adicionar ${addType}`"
        footer=""
        width="90%"
        style="max-width: 1100px; top: 4px"
        :bodyStyle="{ height: 'calc(100vh - 90px)', overflowY: 'auto' }"
        @cancel="onCancel"
    >
        <a-tag color="default">{{ coords.lon }}, {{ coords.lat }}</a-tag>
        <a-tag color="default" v-if="!!selectedLayer">{{ selectedLayer.label }}</a-tag>
        <a-divider style="margin:10px" />
        <div class="modal-top-controls">
            <div class="modal-layer-select">
                <span style="padding-right:10px">Camada:</span>
                <a-select
                    :key="selectedLayerId"
                    v-model:value="selectedLayerId"
                    show-search
                    placeholder="Selecione a camada"
                    style="width: 260px"
                    :options="layersAvailable"
                    :filter-option="filterOption"
                    @change="handleChange"
                />
                <a-upload-dragger
                    name="file"
                    :multiple="true"
                    :show-upload-list="false"
                    :before-upload="beforeUpload"
                    :accept="currentAccept"
                    class="modal-upload-dragger-inline"
                >
                    <div class="modal-upload-dragger-content">
                        <PlusOutlined />
                        <span>Arraste ou clique para enviar</span>
                    </div>
                </a-upload-dragger>
            </div>
        </div>

        <a-divider style="margin: 14px 0 10px 0" />
        <div v-if="pendingUploads.length" class="pending-uploads-block">
            <div class="pending-uploads-header">
                <span style="font-weight:bold">Pré-visualização</span>
                <a-button type="primary" :loading="savingPending" :disabled="!selectedLayer" @click="savePendingUploads">
                    Salvar
                </a-button>
            </div>
            <div class="pending-uploads-grid">
                <div v-for="(item, idx) in pendingUploads" :key="item.uid" class="pending-upload-card">
                    <a-button
                        type="text"
                        danger
                        size="small"
                        class="pending-upload-remove"
                        :disabled="savingPending"
                        @click="removePendingUpload(idx)"
                    >
                        <DeleteOutlined />
                    </a-button>
                    <template v-if="isImagePath(item.name)">
                        <img :src="item.previewUrl" :alt="item.name" class="pending-upload-preview-img" />
                    </template>
                    <template v-else-if="isPdfPath(item.name)">
                        <embed :src="item.previewUrl" type="application/pdf" class="pending-upload-preview-pdf" />
                    </template>
                    <template v-else>
                        <div class="pending-upload-file-placeholder">
                            <i class="fas fa-file-alt"></i>
                            <span>{{ archiveTypeLabel(item.name) }}</span>
                        </div>
                    </template>
                    <div class="pending-upload-name">{{ item.name }}</div>
                </div>
            </div>
        </div>
        <div>
            <span style="font-weight:bold">Visualização em carrossel:</span>
            <div v-if="archivesViewList.length" class="archives-view-carousel-wrap">
                <div class="archives-view-carousel-inner">
                    <a-button
                        v-if="archivesViewList.length > 1"
                        type="text"
                        class="archives-view-arrow archives-view-arrow-prev"
                        aria-label="Anterior"
                        @click="goToPrevArchivesSlide"
                    >
                        <i class="fas fa-chevron-left" aria-hidden="true"></i>
                    </a-button>
                    <a-carousel ref="archivesCarouselRef" effect="fade" class="archives-view-carousel">
                        <div v-for="(item, index) in archivesViewList" :key="`${item.uid}-${index}`" class="archives-view-slide">
                            <div class="archives-view-slide-inner">
                                <a-button
                                    size="small"
                                    danger
                                    class="archives-view-delete-btn"
                                    @click="handleRemove(item)"
                                >
                                    <DeleteOutlined />
                                </a-button>
                                <template v-if="isImagePath(item.url)">
                                    <img :src="item.url" :alt="'Anexo ' + (index + 1)" class="archives-view-img" @error="($e) => $e.target.style.display = 'none'" />
                                </template>
                                <template v-else-if="isPdfPath(item.url)">
                                    <embed v-if="item.url" :src="item.url" type="application/pdf" class="archives-view-pdf" />
                                </template>
                                <template v-else>
                                    <div class="archives-view-file-card">
                                        <i class="fas fa-file-alt archives-view-file-icon"></i>
                                        <span class="archives-view-file-type">{{ archiveTypeLabel(item.name) }}</span>
                                        <a-button type="primary" size="small" @click="openArchiveUrl(item.url)">
                                            <i class="fas fa-external-link-alt"></i> Abrir em nova aba
                                        </a-button>
                                    </div>
                                </template>
                            </div>
                            <div class="archives-view-slide-caption">{{ archiveTypeLabel(item.name) }} ({{ index + 1 }}/{{ archivesViewList.length }})</div>
                        </div>
                    </a-carousel>
                    <a-button
                        v-if="archivesViewList.length > 1"
                        type="text"
                        class="archives-view-arrow archives-view-arrow-next"
                        aria-label="Próximo"
                        @click="goToNextArchivesSlide"
                    >
                        <i class="fas fa-chevron-right" aria-hidden="true"></i>
                    </a-button>
                </div>
            </div>
            <div v-else class="archives-view-empty">Nenhum anexo para visualização.</div>
        </div>
    </a-modal>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue'
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons-vue'
import { openNotification } from '../../../../utils/utilities'
import {
    archiveTypeLabel,
    createPendingUploadItem,
    getAcceptForType,
    isImagePath,
    isPdfPath,
    revokePreviewUrls,
    validateFileMime,
} from '../utils/layerFileHelpers'

const props = defineProps({
    coords: {
        type: Object,
        default: () => ({ lon: '', lat: '' }),
    },
    layersAvailable: {
        type: Array,
        default: () => [],
    },
    geometryHits: {
        type: Array,
        default: () => [],
    },
})

const emit = defineEmits(['reloadFiles', 'filesLoaded'])

const addType = ref('')
const modalOpen = ref(false)
const selectedLayer = ref(null)
const selectedLayerId = ref(null)
const selectedFeatureGid = ref(null)
const fileList = ref({
    documents: [],
    images: [],
})
const otherDocuments = ref([])
const otherImages = ref([])
const archivesCarouselRef = ref(null)
const pendingUploads = ref([])
const savingPending = ref(false)

const currentAccept = computed(() => getAcceptForType(addType.value))

const archivesViewList = computed(() => {
    if (addType.value === 'imagens') {
        return [...fileList.value.images, ...otherImages.value]
    }
    return [...fileList.value.documents, ...otherDocuments.value]
})

const documentCount = computed(() => fileList.value.documents.length)
const imageCount = computed(() => fileList.value.images.length)

watch(
    () => [props.layersAvailable, props.geometryHits],
    () => {
        resetLayerSelection()
    },
    { deep: true }
)

function resetLayerSelection() {
    const layers = props.layersAvailable ?? []
    if (!layers.length) {
        selectedLayer.value = null
        selectedLayerId.value = null
        selectedFeatureGid.value = null
        fileList.value = { documents: [], images: [] }
        otherDocuments.value = []
        otherImages.value = []
        emitFilesLoaded()
        return
    }

    selectedLayer.value = layers[0]
    selectedLayerId.value = layers[0].value
    const hit = props.geometryHits.find(h => String(h.layer_id) === String(selectedLayerId.value))
    selectedFeatureGid.value = hit?.geometry_gid != null ? String(hit.geometry_gid) : null
}

const openArchive = async (type) => {
    addType.value = type
    if (!selectedLayer.value && props.layersAvailable.length > 0) {
        resetLayerSelection()
        await loadFiles()
    }
    modalOpen.value = true
}

const handleChange = async (opt) => {
    selectedLayer.value = props.layersAvailable.find(layer => layer.value === opt)
    const hit = props.geometryHits.find(h => String(h.layer_id) === String(opt))
    selectedFeatureGid.value = hit?.geometry_gid != null ? String(hit.geometry_gid) : null
    await loadFiles()
}

const filterOption = (input, option) => option.value.toLowerCase().indexOf(input.toLowerCase()) >= 0

function revokePendingPreviewUrls() {
    revokePreviewUrls(pendingUploads.value)
    pendingUploads.value = []
}

const removePendingUpload = (index) => {
    const item = pendingUploads.value[index]
    revokePreviewUrls([item])
    pendingUploads.value.splice(index, 1)
}

async function postLayerFile(rawFile) {
    const formData = new FormData()
    const geom = {
        type: 'Point',
        coordinates: [props.coords.lon, props.coords.lat],
    }
    formData.append('type', addType.value)
    formData.append('layer_id', selectedLayer.value.value)
    formData.append('layer_type', selectedLayer.value.geoDocumentLayerType ?? 'geoserver')
    formData.append('geom', JSON.stringify(geom))
    if (selectedFeatureGid.value) formData.append('geometry_gid', selectedFeatureGid.value)
    formData.append('file', rawFile)

    await window.axios.post('/api/map-actions/layers/file', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    })
}

const savePendingUploads = async () => {
    if (!pendingUploads.value.length || !selectedLayer.value) {
        return
    }
    savingPending.value = true
    try {
        for (const item of pendingUploads.value) {
            await postLayerFile(item.raw)
        }
        revokePendingPreviewUrls()
        openNotification('success', 'Anexo(s) salvo(s) com sucesso!')
        await loadFiles()
        reloadFiles()
        await nextTick()
        await nextTick()
        const last = archivesViewList.value.length - 1
        if (last >= 0 && archivesCarouselRef.value?.goTo) {
            archivesCarouselRef.value.goTo(last, false)
        }
    } catch {
        openNotification('error', 'Erro ao salvar documento')
    } finally {
        savingPending.value = false
    }
}

const reloadFiles = () => emit('reloadFiles')

const handleRemove = async file => {
    try {
        await window.axios.delete(`/api/map-actions/layers/file?path=${file.url}`)
        openNotification('success', 'Documento excluido com sucesso!')
        await loadFiles()
        reloadFiles()
    } catch {
        openNotification('error', 'Erro ao excluir documento')
    }
}

const loadFiles = async () => {
    if (!props.coords.lon || !props.coords.lat) {
        return
    }

    const query = new URLSearchParams({
        lon: props.coords.lon,
        lat: props.coords.lat,
    })
    if (selectedFeatureGid.value) query.append('geometry_gid', selectedFeatureGid.value)
    if (selectedLayer.value?.value != null) query.append('layer_id', selectedLayer.value.value)

    const data = (await window.axios.get(`/api/map-actions/layers/file?${query.toString()}`)).data.data

    if (selectedLayer.value?.value != null) {
        fileList.value.documents = data.documents.filter(file => file.layer_id === String(selectedLayer.value.value))
        fileList.value.images = data.images.filter(file => file.layer_id === String(selectedLayer.value.value))
        otherDocuments.value = data.documents.filter(file => file.layer_id !== String(selectedLayer.value.value))
        otherImages.value = data.images.filter(file => file.layer_id !== String(selectedLayer.value.value))
    } else {
        fileList.value = data
        otherDocuments.value = []
        otherImages.value = []
    }

    emitFilesLoaded()
}

function emitFilesLoaded() {
    emit('filesLoaded', {
        documents: fileList.value.documents.length,
        images: fileList.value.images.length,
    })
}

const beforeUpload = (file) => {
    const raw = file.originFileObj ?? file
    const mime = raw.type || file.type

    const mimeCheck = validateFileMime(addType.value, mime)
    if (!mimeCheck.ok) {
        openNotification('error', mimeCheck.message)
        return false
    }

    pendingUploads.value.push(createPendingUploadItem(file))
    return false
}

const onCancel = () => {
    revokePendingPreviewUrls()
    selectedLayer.value = undefined
    selectedLayerId.value = undefined
    selectedFeatureGid.value = null
}

const goToPrevArchivesSlide = () => archivesCarouselRef.value?.prev?.()
const goToNextArchivesSlide = () => archivesCarouselRef.value?.next?.()
const openArchiveUrl = (url) => window.open(url, '_blank')

defineExpose({
    openArchive,
    loadFiles,
    documentCount,
    imageCount,
})
</script>

<style scoped>
.modal-top-controls {
    display: flex;
    gap: 12px;
    align-items: center;
    margin-bottom: 8px;
}

.modal-layer-select {
    min-width: 380px;
    display: flex;
    align-items: center;
    gap: 10px;
}

.modal-upload-dragger-inline {
    width: 260px;
    min-width: 220px;
    margin: 0;
}

.modal-upload-dragger-content {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-size: 12px;
    color: #555;
}

.archives-view-carousel-wrap {
    margin-top: 10px;
}

.archives-view-carousel-inner {
    display: flex;
    align-items: center;
    gap: 6px;
}

.archives-view-carousel {
    flex: 1;
    width: 100%;
    min-height: 420px;
    max-height: 75vh;
    background: #f7f7f7;
    border-radius: 8px;
    padding: 8px;
    overflow: hidden;
}

.archives-view-slide {
    min-height: 300px;
}

.archives-view-slide-inner {
    position: relative;
    height: 62vh;
    min-height: 320px;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
}

.archives-view-delete-btn {
    position: absolute;
    top: 8px;
    right: 8px;
    z-index: 2;
}

.archives-view-img,
.archives-view-pdf {
    max-width: 100%;
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: 6px;
}

.archives-view-file-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
}

.archives-view-file-icon {
    font-size: 42px;
    color: #1677ff;
}

.archives-view-file-type,
.archives-view-slide-caption {
    text-align: center;
    color: #555;
    font-size: 12px;
}

.archives-view-arrow {
    color: #1677ff;
    flex-shrink: 0;
}

.archives-view-empty {
    margin-top: 8px;
    color: #888;
}

.pending-uploads-block {
    margin-bottom: 16px;
    padding: 12px;
    background: #fafafa;
    border: 1px dashed #d9d9d9;
    border-radius: 8px;
}

.pending-uploads-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 12px;
    flex-wrap: wrap;
}

.pending-uploads-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
}

.pending-upload-card {
    position: relative;
    width: 200px;
    min-height: 140px;
    padding: 8px;
    background: #fff;
    border-radius: 6px;
    border: 1px solid #eee;
}

.pending-upload-remove {
    position: absolute;
    top: 2px;
    right: 2px;
    z-index: 2;
}

.pending-upload-preview-img {
    max-width: 100%;
    max-height: 120px;
    object-fit: contain;
    display: block;
    margin: 0 auto;
}

.pending-upload-preview-pdf {
    width: 100%;
    height: 120px;
    border: none;
}

.pending-upload-file-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    min-height: 100px;
    color: #1677ff;
    font-size: 28px;
}

.pending-upload-name {
    margin-top: 6px;
    font-size: 11px;
    color: #666;
    word-break: break-all;
    text-align: center;
}
</style>
